import React, { useState, useMemo, useRef, useEffect } from "react";
import { Form, InputGroup } from "react-bootstrap";

interface Option {
  label: string;
  value: string | number;
  disabled?: boolean;
}

interface GroupedOption {
  group: string;
  options: Option[];
}

interface SubGroup {
  subGroup: string;
  options: Option[];
}

interface NestedGroupedOption {
  group: string;
  subGroups: SubGroup[];
}

interface Props {
  options: Option[] | GroupedOption[] | NestedGroupedOption[];
  values: (string | number)[];
  onChange: (values: (string | number)[]) => void;
  isMultiple?: boolean;
  disabled?: boolean;
  placeholder?: string;
  height?: string;
  nestedGroup?: boolean;
  scrollToSubGroup?: string | null;
  singleSelectPerSubGroup?: boolean;
}

type GroupingType = "flat" | "grouped" | "nested";

const detectGroupingType = (options: any[], nestedGroup?: boolean): GroupingType => {
  if (!options?.length) return "flat";
  if (nestedGroup === true) return "nested";
  if ("subGroups" in options[0]) return "nested";
  if ("group" in options[0]) return "grouped";
  return "flat";
};

const GroupingDropdown: React.FC<Props> = ({
  options,
  values,
  onChange,
  isMultiple = true,
  disabled = false,
  placeholder = "Search...",
  height = "220px",
  nestedGroup,
  scrollToSubGroup,
  singleSelectPerSubGroup = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const subGroupRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!scrollToSubGroup) return;
    const timer = setTimeout(() => {
      const el = subGroupRefs.current[scrollToSubGroup];
      const container = scrollContainerRef.current;
      if (el && container) {
        container.scrollTo({ top: el.offsetTop - container.offsetTop, behavior: "smooth" });
      }
    }, 80);
    return () => clearTimeout(timer);
  }, [scrollToSubGroup, options]);

  const groupingType = useMemo(
    () => detectGroupingType(options as any[], nestedGroup),
    [options, nestedGroup],
  );

  const normalizedValues = useMemo(() => values.map(String), [values]);

  const subGroupValueMap = useMemo(() => {
    if (groupingType !== "nested" || !singleSelectPerSubGroup) return {} as Record<string, (string | number)[]>;
    const map: Record<string, (string | number)[]> = {};
    (options as NestedGroupedOption[]).forEach((group) => {
      group.subGroups.forEach((sub) => {
        map[sub.subGroup] = sub.options.map((opt) => opt.value);
      });
    });
    return map;
  }, [options, groupingType, singleSelectPerSubGroup]);

  const valueToSubGroup = useMemo(() => {
    if (groupingType !== "nested" || !singleSelectPerSubGroup) return {} as Record<string, string>;
    const map: Record<string, string> = {};
    Object.entries(subGroupValueMap).forEach(([subGroup, vals]) => {
      vals.forEach((v) => { map[String(v)] = subGroup; });
    });
    return map;
  }, [subGroupValueMap, groupingType, singleSelectPerSubGroup]);

  const filteredOptions = useMemo(() => {
    const term = searchTerm.toLowerCase();

    if (groupingType === "nested") {
      return (options as NestedGroupedOption[])
        .map((group) => ({
          ...group,
          subGroups: group.subGroups
            .map((sub) => ({
              ...sub,
              options: sub.options.filter((opt) =>
                opt.label.toLowerCase().includes(term),
              ),
            }))
            .filter((sub) => sub.options.length > 0),
        }))
        .filter((group) => group.subGroups.length > 0);
    }

    if (groupingType === "grouped") {
      return (options as GroupedOption[])
        .map((group) => ({
          ...group,
          options: group.options.filter((opt) =>
            opt.label.toLowerCase().includes(term),
          ),
        }))
        .filter((group) => group.options.length > 0);
    }

    return (options as Option[]).filter((opt) =>
      opt.label.toLowerCase().includes(term),
    );
  }, [options, searchTerm, groupingType]);

  const flatFilteredOptions = useMemo(() => {
    if (groupingType === "nested") {
      return (filteredOptions as NestedGroupedOption[]).flatMap((group) =>
        group.subGroups.flatMap((sub) => sub.options),
      );
    }
    if (groupingType === "grouped") {
      return (filteredOptions as GroupedOption[]).flatMap((group) => group.options);
    }
    return filteredOptions as Option[];
  }, [filteredOptions, groupingType]);

  const allValues = flatFilteredOptions?.map((opt) => String(opt.value));

  const isAllSelected =
    allValues?.length > 0 && allValues.every((val) => normalizedValues.includes(val));

  const handleSelect = (value: string | number) => {
    if (disabled) return;
    const valStr = String(value);
    if (isMultiple && singleSelectPerSubGroup && groupingType === "nested") {
      if (normalizedValues.includes(valStr)) {
        onChange(values.filter((v) => String(v) !== valStr));
      } else {
        const subGroup = valueToSubGroup[valStr];
        const subGroupVals = (subGroupValueMap[subGroup] || []).map(String);
        const filtered = values.filter((v) => !subGroupVals.includes(String(v)));
        onChange([...filtered, value]);
      }
    } else if (isMultiple) {
      if (normalizedValues.includes(valStr)) {
        onChange(values.filter((v) => String(v) !== valStr));
      } else {
        onChange([...values, value]);
      }
    } else {
      onChange([value]);
    }
  };

  const handleSelectAll = () => {
    if (disabled) return;
    if (isAllSelected) {
      onChange(values.filter((v) => !allValues.includes(String(v))));
    } else {
      onChange(Array.from(new Set([...values, ...flatFilteredOptions.map((o) => o.value)])));
    }
  };

  const renderOption = (option: Option, subGroupName?: string) => {
    const isChecked = normalizedValues.includes(String(option.value));
    const useRadio =
      !isMultiple ||
      (singleSelectPerSubGroup && groupingType === "nested" && subGroupName !== undefined);
    return (
      <div
        key={option.value}
        className="form-check px-3 py-1 d-flex align-items-center border-bottom"
        onClick={() => !option.disabled && !disabled && handleSelect(option.value)}
        style={{ cursor: option.disabled || disabled ? "not-allowed" : "pointer" }}
      >
        <input
          id={`option-${option.value}`}
          type={useRadio ? "radio" : "checkbox"}
          name={useRadio && subGroupName ? subGroupName : undefined}
          className="form-check-input"
          checked={isChecked}
          onChange={() => handleSelect(option.value)}
          onClick={(e) => e.stopPropagation()}
          disabled={option.disabled || disabled}
        />
        <label
          htmlFor={`option-${option.value}`}
          className="form-check-label ms-2 w-100"
          onClick={(e) => e.preventDefault()}
        >
          {option.label}
        </label>
      </div>
    );
  };

  return (
    <Form.Group>
      <div
        className="bg-white dropdown-container"
        style={{ borderRadius: "10px", boxShadow: "0px 4px 10px rgba(0,0,0,0.1)" }}
      >
        <InputGroup className="p-2 border-bottom position-relative search-box align-items-center">
          {/* {isMultiple && (
            <div className="form-check me-0" style={{ paddingLeft: "9px" }}>
              <input
                type="checkbox"
                className="form-check-input"
                checked={isAllSelected}
                onChange={handleSelectAll}
                disabled={disabled || flatFilteredOptions.length === 0}
              />
            </div>
          )} */}
          <Form.Control
            type="text"
            placeholder={placeholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={disabled}
          />
          {searchTerm.length > 0 && !disabled && (
            <span
              onClick={() => setSearchTerm("")}
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                color: "#aaa",
                fontSize: "18px",
                zIndex: 10,
                userSelect: "none",
              }}
            >
              <span className="material-symbols-outlined">close</span>
            </span>
          )}
        </InputGroup>

        <div ref={scrollContainerRef} className="options-scroll" style={{ maxHeight: height, overflowY: "auto" }}>
          {groupingType === "nested" &&
            (filteredOptions as NestedGroupedOption[]).map((group) => (
              <div key={group.group}>
                <div className="nested-group-header d-flex justify-content-between align-items-center px-3 py-1">
                  <span>{group.group}</span>
                  <span className="badge bg-primary ms-2">
                    {group.subGroups.reduce((sum, s) => sum + s.options.length, 0)}
                  </span>
                </div>
                {group.subGroups.map((sub) => (
                  <div key={sub.subGroup}>
                    <div
                      ref={(el) => { subGroupRefs.current[sub.subGroup] = el; }}
                      className="nested-subgroup-header d-flex justify-content-between align-items-center px-3 py-1"
                    >
                      <span>{sub.subGroup}</span>
                      <span className="badge bg-primary ms-2">{sub.options.length}</span>
                    </div>
                    {sub.options.map((opt) => renderOption(opt, sub.subGroup))}
                  </div>
                ))}
              </div>
            ))}

          {groupingType === "grouped" &&
            (filteredOptions as GroupedOption[]).map((group) => (
              <div key={group.group}>
                <div className="fw-bold px-3 py-1 bg-light">{group.group}</div>
                {group.options.map((opt) => renderOption(opt))}
              </div>
            ))}

          {groupingType === "flat" &&
            (filteredOptions as Option[]).map((opt) => renderOption(opt))}

          {filteredOptions?.length === 0 && (
            <div className="text-center text-muted p-2">No options found</div>
          )}
        </div>
      </div>
    </Form.Group>
  );
};

export default React.memo(GroupingDropdown);
